/**
 * /policies/leave/eligibility-matrix — Session 3c PR 2.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { EligibilityCellItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function LeaveEligibilityMatrixPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-leave-eligibility-matrix", le],
    queryFn: () => session3cApi.listEligibilityMatrix(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<EligibilityCellItem>[] = [
    {
      headerKey: "policies.leave_eligibility.col.leave_type",
      label: "Leave type",
      render: (r) => <span className="font-medium">{r.leave_type_name_en}</span>,
    },
    {
      headerKey: "policies.leave_eligibility.col.grade",
      label: "Grade",
      render: (r) => <span className="text-xs text-text-secondary">{r.grade_name_en}</span>,
    },
    {
      headerKey: "policies.leave_eligibility.col.eligible",
      label: "Eligible",
      render: (r) => (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${r.eligible ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
          {r.eligible ? "yes" : "no"}
        </span>
      ),
    },
    {
      headerKey: "policies.leave_eligibility.col.tenure",
      label: "Min tenure (months)",
      render: (r) => <span className="font-mono text-xs">{r.min_tenure_months ?? "—"}</span>,
    },
    {
      headerKey: "policies.leave_eligibility.col.source",
      label: "Source",
      render: (r) => <span className="text-[10px] uppercase text-text-tertiary">{r.source_layer}</span>,
    },
  ];

  return (
    <PolicyListPage<EligibilityCellItem>
      title={t("policies.leave_eligibility.title", "Leave Eligibility Matrix")}
      subtitle={t("policies.leave_eligibility.subtitle", "Who can take which leave type by grade and tenure. Mutaba'aa cross-checks Article 109 tier minimums.")}
      agent="mutabaa"
      domain="leave_eligibility"
      newButtonLabel={t("policies.leave_eligibility.cta.new", "New Eligibility Override")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => `${r.leave_type_code}-${r.grade_code}`}
      suggestions={[
        {
          headline: t("policies.leave_eligibility.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.leave_eligibility.suggestion.clean_body", "Use the override-stack popover on any cell to see KSA floor → group → LE source."),
          primaryActionLabel: t("policies.leave_eligibility.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "leave-eligibility-matrix-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.leave_eligibility.title", "Leave Eligibility Matrix"),
        window_label: t("policies.leave_eligibility.window", "all leave types × all grades"),
      }}
    />
  );
}
